// Handles EmptyState and ErrorState
import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { Ledger } from '../../../components/ui/Ledger';
import { useGeneralLedger } from '../client';
import { PageHeader } from '../../../components/layout/PageHeader';

export function AccountLedgerView() {
  const { accountId } = useParams({ strict: false });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: response, isLoading, isError, refetch } = useGeneralLedger(accountId as string, { startDate, endDate });
  const lines = response?.data ?? [];

  const handleExportCsv = () => {
    if (lines.length === 0) return;
    const headers = ['Date', 'Entry Number', 'Description', 'Debit (Paise)', 'Credit (Paise)', 'Balance (Paise)'];
    const csvContent = [
      headers.join(','),
      ...lines.map(line => [
        line.entryDate,
        line.entryNumber,
        `"${line.description.replace(/"/g, '""')}"`,
        line.drAmount,
        line.crAmount,
        line.runningBalance
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ledger_${accountId}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const mappedEntries = lines.map(line => ({
    id: line.entryId,
    date: line.entryDate,
    description: line.description,
    debitPaise: line.drAmount > 0 ? line.drAmount : undefined,
    creditPaise: line.crAmount > 0 ? line.crAmount : undefined,
    balancePaise: line.runningBalance,
  }));

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Account Ledger"
        subtitle="View transaction history and running balance."
        action={
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Start date"
              className="w-auto"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="End date"
              className="w-auto"
            />
            <Button onClick={handleExportCsv} disabled={isLoading || lines.length === 0}>Export CSV</Button>
          </div>
        }
      />
      <div className="flex-1 w-full min-h-0">
        {isLoading && (
          <div className="p-8 text-center text-text-secondary">Loading ledger entries...</div>
        )}
        {isError && (
          <div className="p-8 text-center text-danger-600">
            <p className="text-sm font-semibold">Failed to load ledger</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
          </div>
        )}
        {(!isLoading && !isError) && (
          <Ledger entries={mappedEntries} />
        )}
      </div>
    </div>
  );
}